import Overlay from 'ol/Overlay';
import Popup from '../components/Popup.jsx';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Circle} from 'ol/geom';
import {ThemeProvider} from '../theme-provider.jsx';
import {
  buffer,
  getBottomRight,
  getCenter,
  getHeight,
  getTopRight,
  getWidth,
} from 'ol/extent';
import {createRoot} from 'react-dom/client';
import {getFeatureFromEvent} from '../util/map.js';
import {transformExtent} from 'ol/proj';

export function usePopup(
  callback,
  deeplink,
  onSelectResult,
  resultInfo,
  extraOffset = 10,
) {
  const popup = useRef();
  const popupElement = useRef();
  const target = useRef();
  const featureRef = useRef();
  const popupDivRef = useRef();
  const popupRootRef = useRef();
  const centerRef = useRef();
  const [expanded, setExpanded] = useState(false);

  if (!popupDivRef.current) {
    const popupDiv = document.createElement('div');
    popupDiv.style.visibility = 'visible';
    popupDivRef.current = popupDiv;
    const root = createRoot(popupDiv);
    popupRootRef.current = root;
  }

  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const onClose = useCallback(() => {
    if (popup.current && target.current) {
      target.current.removeOverlay(popup.current);
    }
    callback(null);
    popup.current = null;
    centerRef.current = null;
    featureRef.current = null;
    popupDivRef.current = null;
  }, [callback]);

  const onKeyUp = useCallback(
    event => {
      if (event.key === 'Escape' && popup.current) {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    window.addEventListener('keyup', onKeyUp, true);
    return () => {
      window.removeEventListener('keyup', onKeyUp, true);
    };
  }, [onKeyUp]);

  const onMapClick = useCallback(
    event => {
      if (popup.current && target.current) {
        target.current.removeOverlay(popup.current);
      }
      target.current = event.target;
      const feature = getFeatureFromEvent(event);

      if (popup.current && !feature) {
        onClose();
        return;
      }

      if (feature) {
        featureRef.current = feature;
        const fid = feature.get('id');
        onSelectResult(fid);
        popupDivRef.current.style.visibility = 'hidden';
        const featureExtent = feature.getExtent();
        const extent = transformExtent(featureExtent, 'EPSG:3857', 'EPSG:4326');
        callback(event, feature, extent);
        const center = getCenter(extent);
        centerRef.current = center;

        popupRootRef.current.render(
          <ThemeProvider dark>
            <Popup
              ref={popupElement}
              center={center}
              deeplink={deeplink}
              expanded={expanded}
              feature={feature}
              resultInfo={
                resultInfo && fid === resultInfo.id ? resultInfo : undefined
              }
              toggleExpanded={toggleExpanded}
              onClose={onClose}
            />
          </ThemeProvider>,
        );

        popup.current = new Overlay({
          element: popupDivRef.current,
          positioning: 'bottom-center',
        });
        event.target.addOverlay(popup.current);
        const newFeatureExtent = buffer(
          featureExtent,
          getWidth(featureExtent) * 0.15,
        );
        const br = getBottomRight(newFeatureExtent);
        br[1] += getHeight(newFeatureExtent) / 2;
        popup.current.setPosition(br);
        if (resultInfo && fid === resultInfo.id) {
          popup.current.setOffset([
            popupElement.current?.offsetWidth / 2 + extraOffset,
            popupElement.current?.offsetHeight / 2,
          ]);
          popupDivRef.current.style.visibility = 'visible';
        }
        return;
      }

      // no feature, just show the location
      const circle = new Circle(event.coordinate, 24);
      const circleExtent = circle.getExtent();
      const extent = transformExtent(circleExtent, 'EPSG:3857', 'EPSG:4326');

      const center = getCenter(extent);
      centerRef.current = center;

      callback(event, feature, extent);

      popupRootRef.current.render(
        <ThemeProvider dark>
          <Popup
            ref={popupElement}
            center={center}
            deeplink={deeplink}
            expanded={false}
            onClose={onClose}
          />
        </ThemeProvider>,
      );
      popup.current = new Overlay({
        element: popupDivRef.current,
        positioning: 'bottom-center',
      });
      event.target.addOverlay(popup.current);

      const newFeatureExtent = circle.getExtent();

      const br = getTopRight(newFeatureExtent);
      br[1] += getHeight(newFeatureExtent) / 2;
      popup.current.setPosition(br);

      popup.current.setOffset([
        popupElement.current?.offsetWidth / 2 + 16,
        popupElement.current?.offsetHeight / 4,
      ]);
      popupDivRef.current.style.visibility = 'visible';
    },
    [
      callback,
      deeplink,
      expanded,
      extraOffset,
      onClose,
      onSelectResult,
      resultInfo,
      toggleExpanded,
    ],
  );

  useEffect(() => {
    if (!resultInfo || !popupDivRef.current || !centerRef.current) {
      return;
    }

    popupRootRef.current.render(
      <ThemeProvider dark>
        <Popup
          ref={popupElement}
          center={centerRef.current}
          deeplink={deeplink}
          expanded={expanded}
          feature={featureRef.current}
          resultInfo={resultInfo}
          toggleExpanded={toggleExpanded}
          onClose={onClose}
        />
      </ThemeProvider>,
    );
    window.setTimeout(() => {
      if (popup.current) {
        popup.current.setOffset([
          popupElement.current?.offsetWidth / 2 + extraOffset,
          popupElement.current?.offsetHeight / 2,
        ]);
        popupDivRef.current.style.visibility = 'visible';
      }
    });
  }, [deeplink, expanded, extraOffset, onClose, resultInfo, toggleExpanded]);

  return [onMapClick];
}
